import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Label, SearchableSelect } from '@/shared/components';
import type { SelectFilterOption } from '@/features/vehiclesearch/types/manualsearch.types';

interface ModelProps {
    options: SelectFilterOption[];
    modelValue: SelectFilterOption | null;
    onChange: (modelId: string) => void;
    derivativeSelected: boolean;
    disabled: boolean;
    placeholder: string;
}

// A selected derivative overrides the model, so the picker shows cleared while the retained
// filter keeps the model ready to reappear when the derivative is cleared.
export const PlanVehicleRuleModelField: React.FC<ModelProps> = ({
    options,
    modelValue,
    onChange,
    derivativeSelected,
    disabled,
    placeholder,
}) => (
    <div>
        <Label htmlFor="pvr_model_id">
            Model <span className="text-mp-muted">(optional)</span>
        </Label>
        <SearchableSelect<SelectFilterOption>
            id="pvr_model_id"
            options={options}
            value={derivativeSelected ? null : modelValue}
            onChange={(o) => onChange(o?.value ?? '')}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            disabled={disabled}
            clearable
            placeholder={placeholder}
            emptyLabel="No models available."
        />
    </div>
);

interface DerivativeProps<TInput extends FieldValues, TOutput extends FieldValues> {
    control: Control<TInput, unknown, TOutput>;
    options: SelectFilterOption[];
    derivativeValue: SelectFilterOption | null;
    disabled: boolean;
    placeholder: string;
}

export const PlanVehicleRuleDerivativeField = <TInput extends FieldValues, TOutput extends FieldValues>({
    control,
    options,
    derivativeValue,
    disabled,
    placeholder,
}: DerivativeProps<TInput, TOutput>) => (
    <div>
        <Label htmlFor="pvr_derivative_id">
            Derivative <span className="text-mp-muted">(optional — overrides all other dims if set)</span>
        </Label>
        <Controller
            name={'derivative_id' as Path<TInput>}
            control={control}
            render={({ field, fieldState: { error: fieldError } }) => (
                <>
                    <SearchableSelect<SelectFilterOption>
                        id="pvr_derivative_id"
                        options={options}
                        value={derivativeValue}
                        onChange={(o) => field.onChange(o?.value ?? '')}
                        getOptionValue={(o) => o.value}
                        getOptionLabel={(o) => o.label}
                        disabled={disabled}
                        clearable
                        placeholder={placeholder}
                        emptyLabel="No derivatives available."
                    />
                    {fieldError && <span className="text-mp-error text-xs mt-1">{fieldError.message}</span>}
                </>
            )}
        />
    </div>
);
